import { brandsOpenApi } from '@constants';
import { axiosBoa, getErrorMessage } from '@utils';
import { getParent, types } from 'mobx-state-tree';
import { Charge } from './constants';
import { createStoreAction, parseContentDispositionFilename } from './utils';

export const InvoicesStore = types
    .model('InvoicesStore', {
        charges: types.array(Charge),
        chargesMeta: types.frozen(),
        chargesFilters: types.model({
            status: types.maybe(types.number),
            created_at_min: types.maybe(types.string),
            created_at_max: types.maybe(types.string)
        }),
        chargesSorters: types.model({
            sort: types.maybe(types.string),
            order: types.maybe(types.string)
        }),
        payouts: types.array(types.frozen()),
        offsetBalance: types.maybe(types.number),
        payoutsMeta: types.frozen(),
        payoutsFilters: types.model({
            status: types.maybe(types.number),
            created_at_min: types.maybe(types.string),
            created_at_max: types.maybe(types.string)
        }),
        payoutsSorters: types.model({
            sort: types.maybe(types.string),
            order: types.maybe(types.string)
        }),
        statements: types.array(types.frozen()),
        statementsMeta: types.frozen(),
        retryChargeSubmitted: false
    })
    .views((self) => ({
        get root() {
            return getParent(self);
        }
    }))
    .actions((self) => {
        const action = createStoreAction(self);

        return {
            resetPayoutsFilters() {
                self.payoutsFilters = {};
            },
            updatePayoutsFilters(filters) {
                Object.assign(self.payoutsFilters, filters);
            },
            resetPayoutsSorters() {
                self.payoutsSorters = {};
            },
            resetChargesFilters() {
                self.chargesFilters = {};
            },
            updateChargesFilters(filters) {
                Object.assign(self.chargesFilters, filters);
            },
            resetChargesSorters() {
                self.chargesSorters = {};
            },
            updatePayoutsSorters(sorters) {
                Object.assign(self.chargesSorters, sorters);
            },
            updateChargesSorters(sorters) {
                Object.assign(self.chargesSorters, sorters);
            },
            getInvoices: action(function* getInvoices(transactionType, params = {}) {
                if (transactionType === 'charges') {
                    Object.assign(params, self.chargesFilters, self.chargesSorters);
                }

                if (transactionType === 'payouts') {
                    Object.assign(params, self.payoutsFilters, self.payoutsSorters);
                }

                const response = yield axiosBoa.get(brandsOpenApi.invoices.list, {
                    params: {
                        transaction_type: transactionType,
                        ...params
                    }
                });

                if (transactionType !== 'statements') self.offsetBalance = response.meta.offsetBalance;

                self[transactionType] = response.data;
                self[`${transactionType}Meta`] = response.meta;
            }),
            downloadInvoices: action(function* downloadInvoices(params = { transaction_type: 'charges' }) {
                const { transaction_type } = params;

                const configMap = {
                    charges: {
                        meta: self.chargesMeta,
                        filters: self.chargesFilters,
                        sorters: self.chargesSorters
                    },
                    payouts: {
                        meta: self.payoutsMeta,
                        filters: self.payoutsFilters,
                        sorters: self.payoutsSorters
                    }
                };

                const cfg = configMap[transaction_type];
                Object.assign(params, {
                    page: cfg.meta?.page || 1,
                    limit: cfg.meta?.perPage || 10,
                    download: true,
                    ...cfg.filters,
                    ...cfg.sorters
                });

                const { data: file, headers } = yield axiosBoa.get(brandsOpenApi.invoices.list, {
                    params,
                    fullResponse: true
                });

                const fileName = parseContentDispositionFilename(headers['content-disposition']);
                return { file, fileName };
            }),
            downloadPDFInvoices: action(function* downloadPDFInvoices(params = { transaction_type: 'charges' }) {
                Object.assign(params, {
                    download_pdf: true
                });

                const { data: url } = yield axiosBoa.get(brandsOpenApi.invoices.list, {
                    params
                });

                return url;
            }),
            retryFailedInvoiceCharge: action(
                function* retryChargeInvoice({ invoiceId }) {
                    self.retryChargeSubmitted = true; // Prevent multiple retries until job finishes

                    yield axiosBoa.post(brandsOpenApi.invoices.retryCharge, { invoiceId });
                },
                {
                    onError: (error) => {
                        self.root.error = getErrorMessage(error);
                    },
                    onFinally: () => {
                        self.retryChargeSubmitted = false;
                    }
                }
            )
        };
    });
